// @flow
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import DeleteDidItModal from 'app/common/react/components/do/aggregated-pins/did-it/DidItRecommend/DeleteDidItModal';
import DropTargetContainer from 'app/common/react/components/DragDrop/DropTargetContainer';
import HashtagFlyout from 'app/common/react/components/save/pin-to-board/PinPane/HashtagFlyout/HashtagFlyout';
import i18n from 'app/common/lib/i18n';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import Toast from 'app/common/react/ui/Toast';
import type { BasicImage } from 'app/packages/pinterest-flow-types/image';
import withDragDropContext from 'app/common/lib/react/withDragDropContext';
import withShowToast from 'app/common/lib/react/withShowToast';
import { track } from 'app/common/lib/react/analytics';
import { type Thunk } from 'app/common/redux/types';
import {
  createUserDidItData,
  deleteUserDidItData,
  updateUserDidItData,
  type UserDidItCreateData,
  type UserDidItDeleteData,
  type UserDidItUpdateData,
} from 'app/common/redux/actions/user_did_it_data/did_it';
import { bindActionCreators, compose, type Dispatch } from 'redux';
import { Box, Button, Spinner, Text, TextArea } from 'gestalt';

const NOTES_CHAR_LIMIT = 1000;
const TOAST_DURATION = 3000;
const HEIGHT = 366;
const WIDTH = 366;

// A hashtag is #, followed by anything that isn't . #,\/$%\^&\*;:{}=\`~()\[\]@|<>
const LAST_HASHTAG_REGEX = /#[^(. #,\/$%\^&\*;:{}=\`~()\[\]@|<>)]*$/;

export type PinActivity = {|
  id: string,
  imageSignatures: Array<string>,
  notes: string,
  oldImageUrl: string,
|};

type ImageResourceResponse = {|
  resource_response: { data: { image_signature: string } },
|};

type OwnProps = {|
  aggregatedPinDataId: string,
  isDoneByMe: boolean,
  loggedInUser: {
    id: string,
    username: string,
  },
  onDoneStateChanged?: (stateChanged: boolean) => void, // indicates if isDoneByMe has changed
  pinActivity: ?PinActivity, // Indicates an existing user Did It
  pinId: string,
  shareToFacebookCheckedOn: boolean,
  shareToFacebookPermissionGranted: boolean,
|};

type DispatchProps = {|
  createUserDidItData: (UserDidItCreateData) => Thunk<void>,
  deleteUserDidItData: (UserDidItDeleteData) => Thunk<void>,
  updateUserDidItData: (UserDidItUpdateData) => Thunk<void>,
|};

type ReduxProps = {|
  ...OwnProps,
  ...DispatchProps,
|};

type HOCProps = {|
  showToast: (Toast: Object) => void,
|};

type Props = {|
  ...OwnProps,
  ...DispatchProps,
  ...HOCProps,
|};

type State = {|
  id: ?string,
  imageUrlToSend: ?string,
  inTypeahead: boolean,
  notes: string,
  showDeleteModal: boolean,
  submitted: boolean,
  typeaheadValue: string,
|};

export class DidItContent extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    const pinActivity = props.pinActivity || {};

    this.state = {
      id: pinActivity.id || null,
      imageUrlToSend: pinActivity.oldImageUrl || null, // if there is a did it image existing already.
      inTypeahead: false,
      notes: pinActivity.notes || '',
      showDeleteModal: false,
      submitted: false,
      typeaheadValue: '',
    };
  }

  setTextAreaRef = (ref: ?HTMLDivElement) => {
    this._textAreaRef = ref;
  };
  _textAreaRef: ?HTMLDivElement;

  /* =============== Text / Hashtag Functionality =============== */

  handleTextInputChange = ({ value: s }: { value: string }) => {
    this.setState({ notes: s.substring(0, NOTES_CHAR_LIMIT) });
    if (s.length > NOTES_CHAR_LIMIT) {
      // we need to force the component to re-render so the text area shows the
      // shortened comment
      this.forceUpdate();
    } else {
      this.handleHashtagTypeahead(s);
    }
  };

  handleHashtagTypeahead = (s: string) => {
    const hashtags = LAST_HASHTAG_REGEX.exec(s);
    const hashtag = !!hashtags ? hashtags[0] : '';
    if (!this.state.inTypeahead && hashtag) {
      this.setState({ inTypeahead: true, typeaheadValue: hashtag });
    } else if (this.state.inTypeahead) {
      if (hashtag) {
        this.setState({ typeaheadValue: hashtag });
      } else {
        this.setState({ inTypeahead: false, typeaheadValue: '' });
      }
    }
  };

  addHashtag = (s: string) => {
    this.setState((prevState) => ({
      inTypeahead: false,
      notes: prevState.notes.replace(LAST_HASHTAG_REGEX, s),
      typeaheadValue: '',
    }));
  };

  /* =============== Image Handling Functionality =============== */

  uploadImageAndCreateDidIt = () => {
    const options = { image_url: this.state.imageUrlToSend };
    ResourceFactory.create('DidItImageUploadResource', options)
      .callCreate()
      .then((response: ImageResourceResponse) => {
        const {
          data: { image_signature },
        } = response.resource_response;
        return this.createUpdateDidIt(image_signature);
      })
      // Error occurred in uploading the image, should create a fail state. Keeping old code for now.
      .catch((error) => this.createUpdateDidIt());
  };

  handleImageReset = () => this.setState({ imageUrlToSend: null });

  handleFinishImageUpload = (result: { image?: BasicImage }) => {
    if (result.image) {
      this.setState({ imageUrlToSend: result.image.url });
    }
    // TODO(xixia): Error state for image upload failed (upload to AWS, before getting image signature)
  };

  /* =============== Completing the Did It Update / Creation =============== */

  // Kick off creating / finish updating a did it
  handleDoneClick = () => {
    const { pinId } = this.props;

    track(PCONST.thrift.EventType.CLICK, {
      view: PCONST.thrift.ViewType.PIN_DID_IT,
      component: PCONST.thrift.ComponentType.DID_IT_MODAL_FULL_SHEET,
      element: PCONST.thrift.ElementType.BUTTON_SUBMIT,
      objectId: pinId,
    });

    this.setState({ submitted: true });
    this.handleDoneSubmission();
  };

  // If image URL is different from before (user created a new did it or updated a did it), get the
  // image signature again before updating did it.
  handleDoneSubmission = () => {
    const { imageUrlToSend } = this.state;
    const { pinActivity } = this.props;

    if (imageUrlToSend) {
      if (!pinActivity || imageUrlToSend !== pinActivity.oldImageUrl) {
        return this.uploadImageAndCreateDidIt();
      } else if (pinActivity.imageSignatures && pinActivity.imageSignatures.length > 0) {
        return this.createUpdateDidIt(pinActivity.imageSignatures[0]);
      }
    }

    // TODO(xixia): Not sure when this would ever happen / consider the error state.
    return this.createUpdateDidIt();
  };

  createUpdateDidIt(imageSignature?: string) {
    const {
      aggregatedPinDataId,
      createUserDidItData,
      isDoneByMe,
      loggedInUser: { username },
      onDoneStateChanged,
      pinId,
      shareToFacebookCheckedOn,
      shareToFacebookPermissionGranted,
      showToast,
      updateUserDidItData,
    } = this.props;
    const { id, notes } = this.state;

    // Make sure notes (details) is not empty.
    if (!notes) {
      return;
    }

    // Currently no did it exists for this User on this Pin
    if (!isDoneByMe) {
      createUserDidItData({
        aggregatedPinDataId,
        username,
        details: notes,
        image_signatures: imageSignature ? [imageSignature] : [],
        pin_id: pinId,
        publishFacebook: shareToFacebookCheckedOn && shareToFacebookPermissionGranted,
      });
    } else if (id) {
      // User Did It already exists; need to update
      updateUserDidItData({
        id,
        pinId,
        details: notes,
        image_signatures: imageSignature ? [imageSignature] : [],
      });
    }

    // Callback to Parents to indicate that the User Did It was created or updated
    if (onDoneStateChanged) {
      onDoneStateChanged(!isDoneByMe); // Done state (isDoneByMe) only changes if it's a create, not an update.
    }

    // Do not show the toast if already on profile. This will also keep the toast
    // from showing on pin feed if someone edits their try from there, which seems like a rare occurance.
    const isNotOnProfile = window.location.href.indexOf(username + '/tried') === -1;
    if (isNotOnProfile) {
      showToast(({ onHide }) => (
        <Toast
          duration={TOAST_DURATION}
          href={`/${username}/tried/`}
          onHide={onHide}
          text={i18n._(
            "Nice work! Pins you've tried live on your profile",
            'Congratulations after trying a pin',
          )}
        />
      ));
    }
  }

  /* =============== General Interactions =============== */

  handleCancel = () => {
    const { onDoneStateChanged } = this.props;
    if (onDoneStateChanged) {
      onDoneStateChanged(false);
    }
  };

  handleDeleteConfirmation = (userDidItId: ?string) => {
    const {
      aggregatedPinDataId,
      deleteUserDidItData,
      isDoneByMe,
      loggedInUser: { username },
      onDoneStateChanged,
      pinId,
    } = this.props;

    this.setState({ showDeleteModal: false });

    // Did It was only in draft mode; no existing Did Its (edge case: what about the uploaded image?)
    if (!userDidItId) {
      return;
    }

    track(PCONST.thrift.EventType.CLICK, {
      view: PCONST.thrift.ViewType.PIN_DID_IT,
      component: PCONST.thrift.ComponentType.DID_IT_MODAL_FULL_SHEET,
      element: PCONST.thrift.ElementType.DID_IT_CONFIRM_DELETE,
      objectId: pinId,
    });

    deleteUserDidItData({
      aggregatedPinDataId,
      pinId,
      username,
      id: userDidItId,
      isOwnedByMe: true,
    });

    if (onDoneStateChanged) {
      onDoneStateChanged(isDoneByMe); // Done state changed if previously was isDoneByMe then is deleted
    }
  };

  handleShowDeleteModal = () => this.setState({ showDeleteModal: true });

  renderTypeahead = () => {
    if (this.state.inTypeahead && this._textAreaRef) {
      return (
        <HashtagFlyout
          editing
          addHashtag={this.addHashtag}
          anchor={this._textAreaRef}
          query={this.state.typeaheadValue}
          idealDirection="up"
        />
      );
    }
    return null;
  };

  render() {
    const { imageUrlToSend, notes, showDeleteModal, submitted } = this.state;
    const { aggregatedPinDataId, isDoneByMe, pinActivity } = this.props;

    const photoBoxClassName = !!imageUrlToSend ? 'didItPhotoBox transitionUp' : 'didItPhotoBox';
    const textBoxClassName = !!imageUrlToSend ? 'didItTextBox transitionIn' : 'didItTextBox';

    const imgChanged = imageUrlToSend ? (pinActivity || {}).oldImageUrl !== imageUrlToSend : false;
    const hasImg = !!imageUrlToSend;

    const textChanged = pinActivity && pinActivity.notes !== notes;
    const hasText = !!notes && notes.trim() !== '';

    // Essentially, we should only enable the Done Button if there is both an image + text (new tried it)
    // OR changed image or changed text (old tried it)
    const shouldDisableDoneButton = !hasImg || !hasText || (!textChanged && !imgChanged);

    return (
      <Box>
        <Box marginTop={4} paddingX={12}>
          <Box xs={{ column: 12 }}>
            <div className={photoBoxClassName}>
              <Box
                deprecatedMargin={{ bottom: 2 }}
                color="transparent"
                shape="rounded"
                alignItems="center"
                justifyContent="center"
                xs={{ display: 'flex', column: 12 }}
              >
                <DropTargetContainer
                  display="image"
                  existingImageSrc={imageUrlToSend}
                  onImageUploadFinished={this.handleFinishImageUpload}
                  idealErrorDirection="up"
                  onReset={this.handleImageReset}
                  height={HEIGHT}
                  width={WIDTH}
                  uploadSource="pinner"
                />
              </Box>
            </div>
          </Box>
        </Box>
        <div className={textBoxClassName}>
          <Box
            width={366}
            dangerouslySetInlineStyle={{ __style: { marginLeft: 'auto', marginRight: 'auto' } }}
            margin={3}
          >
            <div ref={this.setTextAreaRef}>
              <TextArea
                id="trynotes"
                onChange={this.handleTextInputChange}
                placeholder={i18n._('Add a note or tip', 'Tip input for tried it')}
                value={notes}
              />
            </div>
            {this.renderTypeahead()}
            {notes.length > 900 && (
              <Box deprecatedPadding={{ y: 1 }}>
                <Text align="right" size="sm" smSize="xs" mdSize="xs" lgSize="sm">
                  {' '}
                  {notes.length}/{NOTES_CHAR_LIMIT}{' '}
                </Text>
              </Box>
            )}
          </Box>
        </div>
        <Box display="flex" justifyContent="end">
          {showDeleteModal && (
            <DeleteDidItModal
              didItData={pinActivity}
              aggregatedPinDataId={aggregatedPinDataId}
              onDeleteConfirmation={this.handleDeleteConfirmation}
            />
          )}
          {/* bottom buttons */}
          {submitted ? (
            <Box alignItems="center" marginRight={2}>
              <Spinner
                accessibilityLabel={i18n._(
                  'submit',
                  'accessible label for loading spinner on submit button',
                )}
                show
              />
            </Box>
          ) : null}
          <Box display="flex" marginBottom={3} marginRight={4}>
            {isDoneByMe ? (
              <Box deprecatedMargin={{ right: 1 }}>
                <Button
                  onClick={this.handleShowDeleteModal}
                  text={i18n._('Delete', 'delete button in modal for tried-it info')}
                  inline
                  size="md"
                />
              </Box>
            ) : (
              <Box deprecatedMargin={{ right: 1 }}>
                <Button
                  onClick={this.handleCancel}
                  text={i18n._('Cancel', 'cancel button in modal for tried-it info')}
                  inline
                  size="md"
                />
              </Box>
            )}
            <Box>
              <Button
                color="red"
                disabled={shouldDisableDoneButton}
                onClick={this.handleDoneClick}
                text={i18n._('Done', 'done button in modal for tried-it info')}
                inline
                size="md"
              />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>, ownProps: OwnProps): DispatchProps => ({
  createUserDidItData: bindActionCreators(createUserDidItData, dispatch),
  deleteUserDidItData: bindActionCreators(deleteUserDidItData, dispatch),
  updateUserDidItData: bindActionCreators(updateUserDidItData, dispatch),
});

export default compose(
  connect<ReduxProps, OwnProps, _, _, _, _>(
    null,
    mapDispatchToProps,
  ),
  withDragDropContext,
  withShowToast,
)(DidItContent);
